import { memo, useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { InfoIcon } from '@/components/icons'
import { useWizard } from '@/components/common/wizard/WizardContext'
import useAdsSelection from './hooks/useAdsSelection'
import AudienceStagesList from './AudienceStagesList'
import AdCreativeSelection from './AdCreativeSelection'
import Recommendations from './Recommendations'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

const AdsSelection = memo(() => {
    const { id: campaignId } = useParams()
    const { getStepContentData, setStepContentData } = useWizard()

    // Get campaign objective
    const campaignObjectiveData = getStepContentData('campaignObjective') || {}
    // Ads added to an existing campaign keep the lead source its ads already use
    const campaignLeadSource = campaignObjectiveData.leadSource || null
    const [leadSource, setLeadSource] = useState(campaignLeadSource || 'website_form')
    const selectedObjective = campaignObjectiveData.selectedObjective?.toLowerCase() ?? 'awareness'

    const {
        selectedAudienceForEditing,
        selectedStageForEditing,
        isEditingMode,
        audienceSelections,
        handleEditAudience,
        handleRemoveCreative,
        handleCreativeSelect,
        getSelectedCreativesForAudience,
        getTotalCreativesForStage,
        closeEditingMode,
    } = useAdsSelection()

    // Create a bulk selection handler that updates all at once
    const handleBulkCreativeSelect = useCallback(
        (creativeIds) => {
            if (!selectedAudienceForEditing || !selectedStageForEditing || !creativeIds.length) return

            // Get current data from wizard context
            const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
            const adCreativeSelections = salesFunnelData.adCreativeSelections || {}

            // Build the key for this stage-audience combination
            const stageAudienceKey = `${selectedStageForEditing.id}_${selectedAudienceForEditing.id}`
            const currentSelections = adCreativeSelections[stageAudienceKey] || []

            // Merge with new selections (avoiding duplicates)
            const newSelectionsSet = new Set([...currentSelections, ...creativeIds])
            const finalSelections = Array.from(newSelectionsSet)

            // Update all at once
            const updatedData = {
                ...salesFunnelData,
                adCreativeSelections: {
                    ...adCreativeSelections,
                    [stageAudienceKey]: finalSelections,
                },
            }

            // Set the data in one operation
            setStepContentData(updatedData, 'salesFunnelSetup')
        },
        [selectedAudienceForEditing, selectedStageForEditing, getStepContentData, setStepContentData],
    )

    // Check if there are any selected audiences
    const hasSelectedAudiences = Object.values(audienceSelections).some((audiences) => audiences.length > 0)

    // Check if any creatives are selected (for disabling radio buttons)
    const hasSelectedCreatives = () => {
        const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
        const adCreativeSelections = salesFunnelData.adCreativeSelections || {}
        return Object.values(adCreativeSelections).some((creatives) => creatives && creatives.length > 0)
    }

    // Handle lead source change
    const handleLeadSourceChange = (value) => {
        setLeadSource(value)
    }

    if (!hasSelectedAudiences) {
        return (
            <div className="tw:flex tw:h-full tw:items-center tw:justify-center">
                <div className="tw:text-center tw:text-gray-400">
                    <p className="tw:text-lg tw:font-medium tw:mb-2">No Audiences Selected</p>
                    <p className="tw:text-sm">
                        Please go back to the Sales Funnel Setup step and select audiences first.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="tw:min-h-full tw:flex tw:flex-col">
            {/* Main Content Area with Sidebar */}
            <div className="tw:flex">
                {/* Main Content Area */}
                <div className="tw:pt-6 tw:pb-16 tw:flex-1">
                    {/* Leads Source Section - Only show for leads campaigns */}
                    {selectedObjective === 'leads' && (
                        <div className="tw:px-6 tw:mb-8">
                            <div className="tw:space-y-4">
                                <h3 className="tw:!text-white tw:!text-lg tw:!font-semibold">Leads Source</h3>
                                <p className="tw:!text-white tw:!text-sm">
                                    For Lead campaigns, you must choose a single source to collect leads from. Please
                                    select either an Instant Form or a Website Form.
                                </p>

                                <RadioGroup
                                    value={leadSource}
                                    onValueChange={handleLeadSourceChange}
                                    disabled={hasSelectedCreatives() || Boolean(campaignLeadSource)}
                                    className="tw:space-y-3"
                                >
                                    <div className="tw:flex tw:items-center tw:!space-x-3">
                                        <RadioGroupItem
                                            value="website_form"
                                            id="website_form"
                                            disabled={hasSelectedCreatives() || Boolean(campaignLeadSource)}
                                        />
                                        <label
                                            htmlFor="website_form"
                                            className={`tw:text-white tw:text-sm tw:cursor-pointer ${hasSelectedCreatives() ? 'tw:opacity-50' : ''}`}
                                        >
                                            Get leads from website form
                                        </label>
                                    </div>
                                    <div className="tw:flex tw:items-center tw:!space-x-3">
                                        <RadioGroupItem
                                            value="instant_form"
                                            id="instant_form"
                                            disabled={hasSelectedCreatives() || Boolean(campaignLeadSource)}
                                        />
                                        <label
                                            htmlFor="instant_form"
                                            className={`tw:text-white tw:text-sm tw:cursor-pointer ${hasSelectedCreatives() ? 'tw:opacity-50' : ''}`}
                                        >
                                            Get leads from attached instant form
                                        </label>
                                    </div>
                                </RadioGroup>
                                {campaignLeadSource && (
                                    <p className="tw:!text-gray-300 tw:!text-xs" data-testid="lead-source-locked">
                                        The lead source is the one the existing ads of this campaign use.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="tw:px-6 tw:space-y-6">
                        <div className="tw:text-white tw:text-sm">
                            Please attach at least one creative to each audience. Select an audience to add or edit
                            attached creatives.
                        </div>
                        <div className="tw:flex tw:items-center tw:gap-2">
                            <InfoIcon className="tw:size-4" />
                            <span className="tw:!text-white tw:text-sm">
                                Click on radio button next to audience name to select ad creatives for that audience.
                            </span>
                        </div>
                    </div>

                    {/* Audience Stages List */}
                    <AudienceStagesList
                        audienceSelections={audienceSelections}
                        onEditAudience={handleEditAudience}
                        onCreativeRemove={handleRemoveCreative}
                        getTotalCreativesForStage={getTotalCreativesForStage}
                        getSelectedCreativesForAudience={getSelectedCreativesForAudience}
                    />
                </div>

                {/* Right Sidebar - Recommendations */}
                <div className="tw:border-l tw:border-gray-600 tw:px-10 tw:py-6 tw:w-80 tw:flex-none">
                    <Recommendations getSelectedCreativesForAudience={getSelectedCreativesForAudience} />
                </div>
            </div>

            {/* Full Screen Ad Creative Selection Interface */}
            {isEditingMode && selectedAudienceForEditing && selectedStageForEditing && (
                <div className="tw:w-full tw:min-h-fit">
                    <AdCreativeSelection
                        audience={selectedAudienceForEditing}
                        stage={selectedStageForEditing}
                        selectedCreatives={getSelectedCreativesForAudience(
                            selectedAudienceForEditing.id,
                            selectedStageForEditing.id,
                        )}
                        onCreativeSelect={handleCreativeSelect}
                        onBulkCreativeSelect={handleBulkCreativeSelect}
                        onClose={closeEditingMode}
                        leadSource={leadSource}
                        campaignId={campaignId}
                    />
                </div>
            )}
        </div>
    )
})

AdsSelection.displayName = 'AdsSelection'

export default AdsSelection
